import React, { useEffect, useMemo, useState } from "react";
import { api, fmt } from "../../../services/erpApi";
import { StatCard, PageHeader, LoadingScreen, Modal, EmptyState } from "../../../components/admin/EssUi";
import { useToast } from "../../../components/admin/ToastContext";
import {
  Search, Plus, Ban, RefreshCw, Eye, Banknote,
  ArrowDown, ArrowUp, Scale, Filter, X, Calendar,
  CreditCard, Building2, FileText, Hash, Wallet,
} from "lucide-react";

const PAGE_SIZE = 20;

const dirBadge = (p) => {
  const dir = p.payment_type || (p.move_type === "in_payment" ? "out" : "in");
  if (dir === "in") return { text: "#166534", bg: "#dcfce7", border: "#bbf7d0", label: "Incoming", icon: ArrowDown };
  return { text: "#9a3412", bg: "#ffedd5", border: "#fed7aa", label: "Outgoing", icon: ArrowUp };
};

const stateBadge = (p) => {
  if (p.state === "cancel" || p.state === "cancelled")
    return { text: "#9f1239", bg: "#ffe4e6", border: "#fecdd3", label: "Cancelled", dot: "#e11d48" };
  if (p.posted || p.state === "posted")
    return { text: "#166534", bg: "#dcfce7", border: "#bbf7d0", label: "Posted", dot: "#16a34a" };
  return { text: "#92400e", bg: "#fef3c7", border: "#fde68a", label: (p.state || "draft").toUpperCase(), dot: "#f59e0b" };
};

const inputStyle = {
  width: "100%", padding: "10px 14px", border: "1px solid #e2e8f0",
  borderRadius: "10px", fontSize: "13.5px", outline: "none",
  background: "#fff", boxSizing: "border-box", transition: "all 0.15s ease",
  color: "#1e293b",
};
const labelStyle = { fontSize: "12.5px", fontWeight: "600", color: "#475569", marginBottom: "6px", display: "block", letterSpacing: "0.01em" };

const AdminPanelPaymentsPage = () => {
  const toast = useToast();
  const [payments, setPayments] = useState([]);
  const [journals, setJournals] = useState([]);
  const [partners, setPartners] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const [search, setSearch] = useState("");
  const [dirFilter, setDirFilter] = useState("all");
  const [journalFilter, setJournalFilter] = useState("all");
  const [partnerFilter, setPartnerFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    type: "out_payment", partner_id: "", journal_id: "", amount: "",
    payment_date: new Date().toISOString().slice(0, 10), ref: "",
    payment_method: "bank", invoice_ids: [],
  });

  const [actionId, setActionId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => { setPage(1); }, [search, dirFilter, journalFilter, partnerFilter, dateFrom, dateTo]);

  const load = async () => {
    try {
      const params = { limit: 500 };
      if (dirFilter !== "all") params.type = dirFilter;
      if (journalFilter !== "all") params.journal_id = journalFilter;
      if (partnerFilter !== "all") params.partner_id = partnerFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const rows = await api.payments.list(params);
      setPayments(rows || []);
    } catch { setPayments([]); }
  };

  useEffect(() => {
    (async () => {
      await Promise.all([
        load(),
        api.journals.list({ limit: 500 }).catch(() => []),
        api.partners.list({ limit: 500 }).catch(() => []),
        api.accounting.receivables({ limit: 500, state: "posted" }).catch(() => []),
      ]).then(([, j, p, r]) => {
        setJournals(j || []);
        setPartners(p || []);
        setInvoices(r || []);
      });
      setLoaded(true);
    })();
  }, []);

  const stats = useMemo(() => {
    const incoming = payments.filter((p) => (p.payment_type || (p.move_type === "in_payment" ? "out" : "in")) === "in");
    const outgoing = payments.filter((p) => (p.payment_type || (p.move_type === "in_payment" ? "out" : "in")) === "out");
    const inSum = incoming.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const outSum = outgoing.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    return { count: payments.length, incoming: inSum, outgoing: outSum, net: inSum - outSum };
  }, [payments]);

  const filtered = useMemo(() => payments.filter((p) => {
    if (search.trim()) {
      const q = search.toLowerCase();
      const hay = `${p.name || ""} ${p.partner_name || ""} ${p.journal_name || ""} ${p.ref || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  }), [payments, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const activeFiltersCount = [
    dirFilter !== "all", journalFilter !== "all", partnerFilter !== "all", dateFrom, dateTo,
  ].filter(Boolean).length;

  const resetFilters = () => {
    setDirFilter("all"); setJournalFilter("all"); setPartnerFilter("all");
    setDateFrom(""); setDateTo("");
  };

  const openDetail = async (p) => {
    setDetail(p);
    setDetailLoading(true);
    try {
      const d = await api.payments.get(p.id);
      setDetail(d || p);
    } catch { setDetail(p); }
    setDetailLoading(false);
  };

  const toggleInvoice = (id) => {
    setForm((f) => {
      const has = f.invoice_ids.includes(id);
      return { ...f, invoice_ids: has ? f.invoice_ids.filter((i) => i !== id) : [...f.invoice_ids, id] };
    });
  };

  const selectedInvoices = invoices.filter((i) => form.invoice_ids.includes(i.invoice_id));
  const selectedResidual = selectedInvoices.reduce((s, i) => s + (Number(i.amount_residual) || 0), 0);

  const handleCreate = async () => {
    if (!form.partner_id) { toast("Partner is required", "error"); return; }
    if (!form.journal_id) { toast("Journal is required", "error"); return; }
    const amount = Number(form.amount);
    if (!amount || amount <= 0) { toast("Amount must be greater than 0", "error"); return; }
    if (form.invoice_ids.length > 0 && amount > selectedResidual) {
      toast(`Amount exceeds remaining balance of selected invoices (${fmt.money(selectedResidual)})`, "error");
      return;
    }
    setCreating(true);
    try {
      await api.payments.create({
        type: form.type,
        partner_id: parseInt(form.partner_id),
        journal_id: parseInt(form.journal_id),
        amount,
        payment_date: form.payment_date,
        ref: form.ref.trim() || undefined,
        payment_method: form.payment_method,
        invoice_ids: form.invoice_ids.map((i) => parseInt(i)),
      });
      toast("Payment recorded", "success");
      setShowCreate(false);
      setForm({ type: "out_payment", partner_id: "", journal_id: "", amount: "", payment_date: new Date().toISOString().slice(0, 10), ref: "", payment_method: "bank", invoice_ids: [] });
      await load();
    } catch (e) {
      toast(e.message || "Payment failed", "error");
    }
    setCreating(false);
  };

  const handleCancel = async (p) => {
    setActionId(p.id);
    try {
      await api.payments.cancel(p.id);
      toast(`Payment ${p.name || p.id} cancelled`, "success");
      await load();
    } catch (e) {
      toast(e.message || "Cancel failed", "error");
    }
    setActionId(null);
  };

  const FormField = ({ label, children, icon: Icon }) => (
    <div style={{ marginBottom: "16px" }}>
      <label style={labelStyle}>
        {Icon && <Icon size={12} style={{ display: "inline", marginRight: "5px", verticalAlign: "-2px" }} />}
        {label}
      </label>
      {children}
    </div>
  );

  if (!loaded) return <LoadingScreen />;

  return (
    <div className="max-w-7xl mx-auto animate-fade-in" style={{ padding: "0 16px 40px" }}>
      <PageHeader
        title="Payments"
        subtitle="Treasury — record and cancel customer or supplier payments"
        actions={
          <button
            onClick={() => setShowCreate(true)}
            className="ess-btn ess-btn-primary"
            style={{ display: "inline-flex", alignItems: "center", gap: "6px", boxShadow: "0 4px 12px rgba(34,95,43,0.18)" }}
          >
            <Plus size={16} /> New Payment
          </button>
        }
      />

      {/* Stats */}
      <div className="ess-sg ess-sg4" style={{ marginBottom: "20px" }}>
        <StatCard label="Payments" value={String(stats.count)} icon={Banknote} color="indigo" trend="all recorded" />
        <StatCard label="Incoming" value={fmt.moneyShort(stats.incoming)} icon={ArrowDown} color="green" trend={fmt.money(stats.incoming)} />
        <StatCard label="Outgoing" value={fmt.moneyShort(stats.outgoing)} icon={ArrowUp} color="rose" trend={fmt.money(stats.outgoing)} />
        <StatCard label="Net" value={fmt.moneyShort(stats.net)} icon={Scale} color={stats.net >= 0 ? "blue" : "amber"} trend="in − out" />
      </div>

      {/* Toolbar */}
      <div style={{
        background: "white", borderRadius: "14px", border: "1px solid #e2e8f0",
        padding: "14px 16px", marginBottom: "16px",
        boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
      }}>
        <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: "240px", position: "relative" }}>
            <Search size={16} style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search number, partner, journal, ref…"
              style={{ ...inputStyle, paddingLeft: "40px" }}
            />
          </div>

          <button
            onClick={() => setShowFilters((v) => !v)}
            className="ess-btn ess-btn-secondary"
            style={{ display: "inline-flex", alignItems: "center", gap: "6px", position: "relative" }}
          >
            <Filter size={15} /> Filters
            {activeFiltersCount > 0 && (
              <span style={{
                background: "#225f2b", color: "white", borderRadius: "999px",
                fontSize: "10px", fontWeight: "700", padding: "1px 7px", marginLeft: "2px",
              }}>{activeFiltersCount}</span>
            )}
          </button>

          {activeFiltersCount > 0 && (
            <button
              onClick={resetFilters}
              className="ess-btn ess-btn-secondary"
              style={{ display: "inline-flex", alignItems: "center", gap: "5px", color: "#be123c" }}
            >
              <X size={14} /> Clear
            </button>
          )}
        </div>

        {showFilters && (
          <div style={{
            display: "grid", gap: "10px", marginTop: "14px",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            paddingTop: "14px", borderTop: "1px dashed #e2e8f0",
          }}>
            <select value={dirFilter} onChange={(e) => setDirFilter(e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
              <option value="all">All Directions</option>
              <option value="in">Incoming</option>
              <option value="out">Outgoing</option>
            </select>
            <select value={journalFilter} onChange={(e) => setJournalFilter(e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
              <option value="all">All Journals</option>
              {journals.map((j) => <option key={j.id} value={j.id}>{j.name}</option>)}
            </select>
            <select value={partnerFilter} onChange={(e) => setPartnerFilter(e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
              <option value="all">All Partners</option>
              {partners.map((p) => <option key={p.id} value={p.id}>{p.display_name || p.name}</option>)}
            </select>
            <div style={{ position: "relative" }}>
              <Calendar size={14} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none" }} />
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={{ ...inputStyle, paddingLeft: "34px" }} placeholder="From" />
            </div>
            <div style={{ position: "relative" }}>
              <Calendar size={14} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none" }} />
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={{ ...inputStyle, paddingLeft: "34px" }} placeholder="To" />
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div style={{
        background: "white", borderRadius: "14px", border: "1px solid #e2e8f0",
        overflow: "hidden", boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
      }}>
        {paginated.length === 0 ? (
          <EmptyState icon="💰" title="No payments" subtitle="Payments will appear here" />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  <th className="ess-th" style={{ minWidth: "140px" }}>Number</th>
                  <th className="ess-th" style={{ minWidth: "100px" }}>Date</th>
                  <th className="ess-th" style={{ minWidth: "120px" }}>Journal</th>
                  <th className="ess-th" style={{ minWidth: "140px" }}>Partner</th>
                  <th className="ess-th" style={{ minWidth: "160px" }}>Invoice</th>
                  <th className="ess-th ess-th-r" style={{ minWidth: "110px" }}>Amount</th>
                  <th className="ess-th" style={{ minWidth: "110px", textAlign: "center" }}>Direction</th>
                  <th className="ess-th" style={{ minWidth: "110px", textAlign: "center" }}>State</th>
                  <th className="ess-th ess-th-r" style={{ minWidth: "110px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((p, index) => {
                  const db = dirBadge(p);
                  const sb = stateBadge(p);
                  const DirIcon = db.icon;
                  const isCancelled = p.state === "cancel" || p.state === "cancelled";
                  return (
                    <tr key={p.id} className="ess-tr" style={{ background: index % 2 === 1 ? "#fafbfc" : "white" }}>
                      <td className="ess-td" style={{ fontWeight: "600", whiteSpace: "nowrap", color: "#0f172a" }}>
                        {p.name || `PAY ${p.id}`}
                      </td>
                      <td className="ess-td" style={{ color: "#64748b", whiteSpace: "nowrap" }}>
                        {fmt.date(p.date || p.payment_date)}
                      </td>
                      <td className="ess-td" style={{ color: "#475569" }}>{p.journal_name || "—"}</td>
                      <td className="ess-td" style={{ color: "#475569" }}>{p.partner_name || "—"}</td>
                      <td className="ess-td" style={{ color: "#64748b", fontSize: "12.5px" }}>
                        {p.invoice_name || (p.invoice_ids || []).join(", ") || "—"}
                      </td>
                      <td className="ess-td" style={{ fontWeight: "700", textAlign: "right", whiteSpace: "nowrap", color: "#0f172a" }}>
                        {fmt.money(p.amount)}
                      </td>
                      <td className="ess-td" style={{ textAlign: "center" }}>
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: "5px",
                          background: db.bg, color: db.text, border: `1px solid ${db.border}`,
                          padding: "3px 10px", borderRadius: "999px",
                          fontSize: "11.5px", fontWeight: "600", whiteSpace: "nowrap",
                        }}>
                          <DirIcon size={11} /> {db.label}
                        </span>
                      </td>
                      <td className="ess-td" style={{ textAlign: "center" }}>
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: "6px",
                          background: sb.bg, color: sb.text, border: `1px solid ${sb.border}`,
                          padding: "3px 10px", borderRadius: "999px",
                          fontSize: "11.5px", fontWeight: "600", whiteSpace: "nowrap",
                        }}>
                          <span style={{ width: "6px", height: "6px", borderRadius: "999px", background: sb.dot }} />
                          {sb.label}
                        </span>
                      </td>
                      <td className="ess-td" style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        <div style={{ display: "flex", gap: "6px", justifyContent: "flex-end" }}>
                          <button
                            title="View"
                            onClick={() => openDetail(p)}
                            className="ess-btn ess-btn-secondary"
                            style={{ padding: "6px 9px", borderRadius: "8px" }}
                          >
                            <Eye size={13} />
                          </button>
                          {!isCancelled && (
                            <button
                              title="Cancel payment"
                              onClick={() => handleCancel(p)}
                              disabled={actionId === p.id}
                              className="ess-btn ess-btn-danger"
                              style={{ padding: "6px 9px", borderRadius: "8px" }}
                            >
                              {actionId === p.id ? <RefreshCw size={13} className="animate-spin" /> : <Ban size={13} />}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "12px 18px", borderTop: "1px solid #e2e8f0", background: "#fafbfc",
          flexWrap: "wrap", gap: "10px",
        }}>
          <span style={{ fontSize: "12.5px", color: "#64748b" }}>
            Showing <strong style={{ color: "#334155" }}>{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)}</strong> of <strong style={{ color: "#334155" }}>{filtered.length}</strong>
          </span>
          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="ess-btn ess-btn-secondary" style={{ borderRadius: "8px" }}>← Prev</button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const start = Math.max(1, Math.min(page - 2, totalPages - 4));
              const p = start + i;
              if (p > totalPages) return null;
              return (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={p === page ? "ess-btn ess-btn-primary" : "ess-btn ess-btn-secondary"}
                  style={{ borderRadius: "8px", minWidth: "34px" }}
                >
                  {p}
                </button>
              );
            })}
            <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="ess-btn ess-btn-secondary" style={{ borderRadius: "8px" }}>Next →</button>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="New Payment" size="lg">
        <div>
          <FormField label="Payment Type *">
            <div style={{ display: "grid", gap: "10px" }} className="ess-rg-2">
              <button type="button" onClick={() => setForm((f) => ({ ...f, type: "out_payment", invoice_ids: [] }))} style={typeBtn(form.type === "out_payment", "#16a34a", "#f0fdf4")}>
                <ArrowDown size={15} /> Encaissement client (in)
              </button>
              <button type="button" onClick={() => setForm((f) => ({ ...f, type: "in_payment", invoice_ids: [] }))} style={typeBtn(form.type === "in_payment", "#e11d48", "#fef2f2")}>
                <ArrowUp size={15} /> Décaissement fournisseur (out)
              </button>
            </div>
          </FormField>

          <div style={{ display: "grid", gap: "14px" }} className="ess-rg-2">
            <FormField label="Partner *" icon={Building2}>
              <select value={form.partner_id} onChange={(e) => setForm((f) => ({ ...f, partner_id: e.target.value }))} style={inputStyle}>
                <option value="">Select partner…</option>
                {partners.map((p) => <option key={p.id} value={p.id}>{p.display_name || p.name}</option>)}
              </select>
            </FormField>
            <FormField label="Journal *" icon={FileText}>
              <select value={form.journal_id} onChange={(e) => setForm((f) => ({ ...f, journal_id: e.target.value }))} style={inputStyle}>
                <option value="">Select journal…</option>
                {journals.map((j) => <option key={j.id} value={j.id}>{j.name}</option>)}
              </select>
            </FormField>
          </div>

          <div style={{ display: "grid", gap: "14px" }} className="ess-rg-2">
            <FormField label="Amount *" icon={Wallet}>
              <input type="number" min="0" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="0" style={inputStyle} />
            </FormField>
            <FormField label="Payment Method" icon={CreditCard}>
              <select value={form.payment_method} onChange={(e) => setForm((f) => ({ ...f, payment_method: e.target.value }))} style={inputStyle}>
                <option value="bank">Bank</option>
                <option value="cash">Cash</option>
                <option value="cheque">Cheque</option>
                <option value="transfer">Transfer</option>
              </select>
            </FormField>
          </div>

          <div style={{ display: "grid", gap: "14px" }} className="ess-rg-2">
            <FormField label="Payment Date" icon={Calendar}>
              <input type="date" value={form.payment_date} onChange={(e) => setForm((f) => ({ ...f, payment_date: e.target.value }))} style={inputStyle} />
            </FormField>
            <FormField label="Reference" icon={Hash}>
              <input value={form.ref} onChange={(e) => setForm((f) => ({ ...f, ref: e.target.value }))} placeholder="CHQ001" style={inputStyle} />
            </FormField>
          </div>

          {form.type === "out_payment" ? (
            <FormField label="Link to customer invoice(s) (optional)">
              <div style={{ border: "1px solid #e2e8f0", borderRadius: "12px", padding: "8px", maxHeight: "220px", overflowY: "auto", background: "#fafbfc" }}>
                {invoices.length === 0 ? (
                  <p style={{ fontSize: "13px", color: "#94a3b8", padding: "12px", textAlign: "center" }}>No open receivables found</p>
                ) : (
                  invoices.map((inv) => {
                    const checked = form.invoice_ids.includes(inv.invoice_id);
                    return (
                      <label key={inv.invoice_id} style={{
                        display: "flex", alignItems: "center", gap: "10px",
                        padding: "10px 12px", borderRadius: "8px", cursor: "pointer",
                        fontSize: "13px", background: checked ? "#f0fdf4" : "transparent",
                        border: checked ? "1px solid #bbf7d0" : "1px solid transparent",
                        marginBottom: "4px", transition: "all 0.12s ease",
                      }}>
                        <input type="checkbox" checked={checked} onChange={() => toggleInvoice(inv.invoice_id)} style={{ accentColor: "#16a34a" }} />
                        <span style={{ fontWeight: "600", color: "#334155", flex: 1 }}>{inv.invoice_name || `INV ${inv.invoice_id}`}</span>
                        <span style={{ color: "#64748b", fontSize: "12px" }}>{inv.partner_name || ""}</span>
                        <strong style={{ color: checked ? "#16a34a" : "#475569", whiteSpace: "nowrap" }}>{fmt.money(inv.amount_residual)}</strong>
                      </label>
                    );
                  })
                )}
              </div>
              {selectedInvoices.length > 0 && (
                <div style={{
                  fontSize: "13px", marginTop: "10px", padding: "10px 14px",
                  background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "10px",
                  display: "flex", justifyContent: "space-between", alignItems: "center", color: "#166534",
                }}>
                  <span>{selectedInvoices.length} invoice{selectedInvoices.length > 1 ? "s" : ""} selected</span>
                  <span>Remaining: <strong>{fmt.money(selectedResidual)}</strong></span>
                </div>
              )}
            </FormField>
          ) : (
            <div style={{
              fontSize: "13px", color: "#64748b", marginBottom: "16px",
              padding: "12px 14px", background: "#f8fafc", borderRadius: "10px",
              border: "1px dashed #cbd5e1",
            }}>
              Free supplier payment — link to a vendor bill is handled on the Vendor Bills page.
            </div>
          )}

          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", borderTop: "1px solid #e2e8f0", paddingTop: "20px", marginTop: "4px" }}>
            <button onClick={() => setShowCreate(false)} className="ess-btn ess-btn-secondary" style={{ borderRadius: "10px" }}>Cancel</button>
            <button onClick={handleCreate} disabled={creating} className="ess-btn ess-btn-primary" style={{ borderRadius: "10px", boxShadow: "0 4px 12px rgba(34,95,43,0.2)" }}>
              {creating ? "Recording…" : "Record Payment"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Detail Modal */}
      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail ? `${detail.name || `PAY ${detail.id}`}` : ""} size="lg">
        {detail && (
          <div>
            <div style={{
              display: "grid", gap: "10px", marginBottom: "18px",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              padding: "14px 16px", background: "#f8fafc", borderRadius: "12px",
              border: "1px solid #e2e8f0",
            }}>
              <DetailItem label="Partner" value={detail.partner_name || "—"} icon={Building2} />
              <DetailItem label="Journal" value={detail.journal_name || "—"} icon={FileText} />
              <DetailItem label="Date" value={fmt.date(detail.date || detail.payment_date)} icon={Calendar} />
              <DetailItem label="Method" value={detail.payment_method || "—"} icon={CreditCard} />
              <DetailItem label="Reference" value={detail.ref || "—"} icon={Hash} />
              <DetailItem label="Amount" value={fmt.money(detail.amount)} icon={Wallet} highlight />
            </div>

            {detailLoading ? (
              <LoadingScreen />
            ) : (detail.lines || []).length === 0 ? (
              <EmptyState icon="📭" title="No lines" subtitle="This payment has no lines" />
            ) : (
              <div style={{ overflowX: "auto", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#f8fafc" }}>
                      <th className="ess-th" style={{ minWidth: "180px" }}>Account</th>
                      <th className="ess-th ess-th-r" style={{ minWidth: "100px" }}>Debit</th>
                      <th className="ess-th ess-th-r" style={{ minWidth: "100px" }}>Credit</th>
                      <th className="ess-th" style={{ minWidth: "70px", textAlign: "center" }}>Rec.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail.lines || []).map((l, index) => (
                      <tr key={index} className="ess-tr" style={{ background: index % 2 === 1 ? "#fafbfc" : "white" }}>
                        <td className="ess-td" style={{ fontFamily: "ui-monospace, monospace", color: "#225f2b", whiteSpace: "nowrap", fontSize: "12.5px" }}>
                          {l.account_code || l.account_id} — {l.account_name || ""}
                        </td>
                        <td className="ess-td" style={{ color: "#16a34a", textAlign: "right", whiteSpace: "nowrap", fontWeight: l.debit ? "600" : "400" }}>
                          {l.debit ? fmt.money(l.debit) : "—"}
                        </td>
                        <td className="ess-td" style={{ color: "#be123c", textAlign: "right", whiteSpace: "nowrap", fontWeight: l.credit ? "600" : "400" }}>
                          {l.credit ? fmt.money(l.credit) : "—"}
                        </td>
                        <td className="ess-td" style={{ textAlign: "center" }}>
                          {l.reconciled ? (
                            <span style={{
                              display: "inline-flex", alignItems: "center", justifyContent: "center",
                              width: "20px", height: "20px", borderRadius: "999px",
                              background: "#dcfce7", color: "#16a34a", fontWeight: "700", fontSize: "12px",
                            }}>✓</span>
                          ) : (
                            <span style={{ color: "#cbd5e1" }}>—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

const DetailItem = ({ label, value, icon: Icon, highlight }) => (
  <div>
    <div style={{ fontSize: "11px", color: "#94a3b8", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.04em", display: "flex", alignItems: "center", gap: "4px", marginBottom: "3px" }}>
      {Icon && <Icon size={11} />} {label}
    </div>
    <div style={{ fontSize: "13.5px", color: highlight ? "#225f2b" : "#1e293b", fontWeight: highlight ? "700" : "500" }}>
      {value}
    </div>
  </div>
);

const typeBtn = (active, color, bg) => ({
  padding: "12px 14px", border: active ? `2px solid ${color}` : "1px solid #e2e8f0",
  borderRadius: "10px", background: active ? bg : "white",
  color: active ? color : "#475569", fontWeight: "600", fontSize: "13.5px", cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
  transition: "all 0.15s ease", boxShadow: active ? `0 2px 8px ${color}22` : "none",
});

export default AdminPanelPaymentsPage;